import * as React from 'react';
import * as CollapsiblePrimitive from '@radix-ui/react-collapsible';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronDownIcon } from 'lucide-react';

import { cn } from '../../lib/utils';

type AnimatedCollapsibleSectionProps = {
  title: React.ReactNode;
  children: React.ReactNode;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  className?: string;
  headerClassName?: string;
  contentClassName?: string;
  actions?: React.ReactNode;
};

export function AnimatedCollapsibleSection({
  title,
  children,
  open,
  defaultOpen = true,
  onOpenChange,
  className,
  headerClassName,
  contentClassName,
  actions,
}: AnimatedCollapsibleSectionProps) {
  const isControlled = typeof open === 'boolean';
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState<boolean>(defaultOpen);

  const isOpen = isControlled ? (open as boolean) : uncontrolledOpen;

  const setOpen = React.useCallback(
    (next: boolean) => {
      if (!isControlled) setUncontrolledOpen(next);
      onOpenChange?.(next);
    },
    [isControlled, onOpenChange]
  );

  return (
    <CollapsiblePrimitive.Root open={isOpen} onOpenChange={setOpen} className={cn('rounded-lg border border-border/60', className)}>
      <div className={cn('flex items-center justify-between gap-2 px-3 py-2 bg-muted/10', headerClassName)}>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium text-foreground/90 truncate">{title}</div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {actions}
          <CollapsiblePrimitive.Trigger
            type="button"
            className={cn(
              'inline-flex items-center justify-center rounded-md border border-border bg-background px-2 py-1 text-muted-foreground hover:text-foreground transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2'
            )}
            aria-label={isOpen ? 'Collapse section' : 'Expand section'}
          >
            <ChevronDownIcon className={cn('h-4 w-4 transition-transform duration-200', isOpen ? 'rotate-180' : '')} />
          </CollapsiblePrimitive.Trigger>
        </div>
      </div>

      <AnimatePresence initial={false}>
        {isOpen && (
          <CollapsiblePrimitive.Content forceMount asChild>
            <motion.div
              initial={{ height: 0, opacity: 0, overflow: 'hidden' }}
              animate={{ 
                height: 'auto', 
                opacity: 1,
                transitionEnd: { overflow: 'visible' }
              }}
              exit={{ height: 0, opacity: 0, overflow: 'hidden' }}
              transition={{ duration: 0.18, ease: 'easeOut' }}
              className={cn(contentClassName)}
            >
              <div className="p-3">{children}</div>
            </motion.div>
          </CollapsiblePrimitive.Content>
        )}
      </AnimatePresence>
    </CollapsiblePrimitive.Root>
  );
}
